import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Checks whether an artwork URL is a Breakin Bread logo or empty.
 * Returns true if the URL is invalid or is a BB logo (never use logo as track artwork).
 */
export function isInvalidOrLogoArtwork(url?: string | null): boolean {
  if (!url || typeof url !== 'string' || !url.trim()) return true;
  const lower = url.toLowerCase().trim();
  return (
    lower.includes('bb-logo') ||
    lower.includes('logo.png') ||
    lower.includes('logo.svg') ||
    lower.includes('/logos/')
  );
}

/**
 * Returns a valid artwork URL if present and NOT a BB logo. Otherwise returns undefined.
 */
export function cleanArtworkUrl(url?: string | null): string | undefined {
  if (isInvalidOrLogoArtwork(url)) return undefined;
  return url!.trim();
}

/**
 * Resolves track artwork with hierarchy:
 * 1. Track/work specific artwork (coverArtUrl, imageUrl, etc.)
 * 2. Artist profile/avatar thumbnail
 * 3. Creator/owner profile thumbnail
 * 4. NEVER returns BB logo (returns undefined for clean placeholder fallback)
 */
export function resolveTrackArtwork(trackOrWork: any, fallbackArtistImage?: string | null): string | undefined {
  if (!trackOrWork) return cleanArtworkUrl(fallbackArtistImage);
  const raw = typeof trackOrWork === 'object' ? trackOrWork : {};

  const candidates = [
    raw.coverArtUrl,
    raw.thumbnailUrl,
    raw.imageUrl,
    raw.artworkUrl,
    raw.artwork,
    raw.coverUrl,
    raw.cover,
    fallbackArtistImage,
    raw.artistAvatarUrl,
    raw.artistPhotoUrl,
    raw.artistImageUrl,
    raw.artist?.avatarUrl,
    raw.artist?.photoUrl,
    raw.artist?.imageUrl,
    raw.creator?.avatarUrl,
    raw.creator?.photoUrl,
    raw.creator?.imageUrl,
    raw.owner?.avatarUrl,
    raw.owner?.profileImageUrl,
  ];

  for (const candidate of candidates) {
    const cleaned = cleanArtworkUrl(candidate);
    if (cleaned) return cleaned;
  }

  return undefined;
}

